import React, { ReactNode } from 'react'
import Page from 'layouts/Page'
import PageWrapper from '../../../PageWrapper'
import { Link } from 'react-router'
import {
  CreditCardOutlined,
  DollarCircleOutlined,
  FolderOutlined,
} from '@ant-design/icons'
import styles from './FinanceLanding.module.scss'
import RequirePermission from 'enrollment-platform/rbac/RequirePermission'
import {
  RbacActions,
  RbacSubjects,
  type RbacAction,
  type RbacSubject,
} from 'enrollment-platform/rbac/constants'
import useFeatureFlags, { type UseFeatureFlags } from 'hooks/useFeatureFlags'

type FeatureFlags = UseFeatureFlags['flags']

interface FinanceLink {
  label: string
  path: string
  /** When set, the tile is hidden unless the user has this ability. */
  requiredPermission?: { action: RbacAction; subject: RbacSubject }
  /** When set, the tile is hidden unless this feature flag is on. */
  featureFlag?: keyof FeatureFlags
}

interface FinanceSection {
  title: string
  icon: ReactNode
  links: FinanceLink[]
}

function FinanceLandingSection({
  title,
  icon,
  links,
  basePath,
  flags,
}: FinanceSection & { basePath: string; flags: FeatureFlags }) {
  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <span className={styles.sectionIcon}>{icon}</span>
        <span className={styles.sectionTitle}>{title}</span>
      </div>
      <nav className={styles.sectionLinks}>
        {links.map(({ label, path, requiredPermission, featureFlag }) => {
          if (featureFlag && !flags[featureFlag]) return null
          const link = (
            <Link
              key={path}
              to={`${basePath}/${path}`}
              className={styles.sectionLink}
            >
              {label}
            </Link>
          )
          if (!requiredPermission) return link
          return (
            <RequirePermission
              key={path}
              action={requiredPermission.action}
              subject={requiredPermission.subject}
            >
              {link}
            </RequirePermission>
          )
        })}
      </nav>
    </section>
  )
}

const financeSections: FinanceSection[] = [
  {
    title: 'Monthly Payment Management',
    icon: <CreditCardOutlined />,
    links: [
      { label: 'Payment Assignment', path: 'payment-assignment' },
      {
        label: 'Primary Payment Method Batch Update',
        path: 'payment-batch-update',
      },
      { label: 'VCN Management', path: 'vcn-management' },
    ],
  },
  {
    title: 'Reconciliation Management',
    icon: <DollarCircleOutlined />,
    links: [
      {
        label: 'Payment Reconciliation',
        path: 'payment-reconciliation-v2',
        featureFlag: 'admin.finance.reconciliation-v2-enabled',
      },
      {
        label: 'Reconciliation Reports',
        path: 'payment-reconciliation-v2/reports',
        featureFlag: 'admin.finance.reconciliation-v2-reports-enabled',
      },
      { label: 'Payment Bulk File Upload', path: 'payment-bulk-upload' },
    ],
  },
  {
    title: 'Funding Invoice Management',
    icon: <FolderOutlined />,
    links: [
      {
        label: 'Billing Profiles',
        path: 'billing-profiles',
        requiredPermission: {
          action: RbacActions.Read,
          subject: RbacSubjects.BillingProfile,
        },
      },
      {
        label: 'Invoice Management',
        path: 'invoice-management',
        requiredPermission: {
          action: RbacActions.Manage,
          subject: RbacSubjects.InvoiceManagement,
        },
      },
      {
        label: 'Benefit Effective Report',
        path: 'benefit-effective-report',
        // The report lists employees.
        requiredPermission: {
          action: RbacActions.Read,
          subject: RbacSubjects.UsRestrictedData,
        },
      },
    ],
  },
]

export default function Landing({
  router,
}: {
  router: { location: { pathname: string } }
}) {
  const basePath = router.location.pathname.replace(/\/+$/, '')
  const { flags } = useFeatureFlags()

  return (
    <PageWrapper>
      <Page title="Finance" breadcrumb={null}>
        <div className={styles.sections}>
          {financeSections.map((section) => (
            <FinanceLandingSection
              key={section.title}
              {...section}
              basePath={basePath}
              flags={flags}
            />
          ))}
        </div>
      </Page>
    </PageWrapper>
  )
}
